import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'react-bootstrap';
import Button from 'components/button/Button';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import useAppModal from 'hooks/useAppModal';
import useFetchProperties from '../../common/useFetchProperties';
import PropertiesSelectTable from '../../common/propertiesTable/PropertiesSelectTable';

export const PROPERTIES_SELECT_MODAL_ID = 'properties-select-modal-details';

const PropertiesSelectModal = ({
  selectedProperties,
  onUpdate,
}: {
  selectedProperties: string[];
  onUpdate: (properties: string[]) => void;
}) => {
  const { t } = useTranslation();
  const { closeModal } = useAppModal();
  const { allProperties, isFetching } = useFetchProperties();

  const [checkedProperties, setCheckedProperties] = useState(() => {
    const map: Record<string, boolean> = {};
    allProperties?.forEach((property) => {
      map[property.uid] = selectedProperties.includes(property.uid);
    });
    return map;
  });

  const allSelected = useMemo(() => {
    if (!allProperties?.length) {
      return false;
    }
    return allProperties.every((p) => checkedProperties[p.uid]);
  }, [allProperties, checkedProperties]);

  const handleSelectAll = () => {
    const newValue = !allSelected;
    const updated: Record<string, boolean> = {};
    allProperties?.forEach((property) => {
      updated[property.uid] = newValue;
    });
    setCheckedProperties(updated);
  };

  const handlePropertyToggle = (uid: string, checked: boolean) => {
    setCheckedProperties((prev) => ({
      ...prev,
      [uid]: checked,
    }));
  };

  const handleApply = () => {
    const selected = Object.entries(checkedProperties)
      .filter(([, isChecked]) => isChecked)
      .map(([uid]) => uid);

    onUpdate(selected);

    closeModal(PROPERTIES_SELECT_MODAL_ID);
  };

  // apply is disabled if no properties are selected
  const applyDisabled = useMemo(() => {
    return Object.values(checkedProperties).filter(Boolean).length === 0;
  }, [checkedProperties]);

  const handleClose = () => {
    closeModal(PROPERTIES_SELECT_MODAL_ID);
  };

  if (isFetching) {
    return <LoadingPlaceholder />;
  }

  return (
    <div>
      <Modal.Header closeButton>
        <Modal.Title>
          {t('pageScreenAndProtect.detailsPage.propertiesSelectModal.title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          {t(
            'pageScreenAndProtect.detailsPage.propertiesSelectModal.description',
          )}
        </p>
        <PropertiesSelectTable
          properties={allProperties ?? []}
          checkedProperties={checkedProperties}
          allSelected={allSelected}
          onSelectAll={handleSelectAll}
          onPropertyToggle={handlePropertyToggle}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          bsStyle="default"
          onClick={handleClose}
          data-testid="cancel-properties-select-modal"
        >
          {t('common.cancel')}
        </Button>
        <Button
          bsStyle="primary"
          onClick={handleApply}
          disabled={applyDisabled}
          data-testid="apply-selected-properties-button"
        >
          {t(
            'pageScreenAndProtect.detailsPage.propertiesSelectModal.applySelectedProperties',
          )}
        </Button>
      </Modal.Footer>
    </div>
  );
};

export default PropertiesSelectModal;
